import type { AppError } from '@/types/index';

/**
 * Maps Firebase error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  'auth/email-already-in-use': 'This email is already registered.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/operation-not-allowed': 'Email/password authentication is not enabled.',
  'auth/popup-blocked': 'Popup was blocked. Please allow popups and try again.',
  'auth/popup-closed-by-user': 'Sign-in was canceled before completion.',
  'auth/cancelled-popup-request': 'Another sign-in is already in progress.',

  // Validation errors
  'validation/invalid-weight': 'Weight must be between 20 and 300 kg.',
  'validation/future-date': 'Date cannot be in the future.',
  'validation/too-many-photos': 'Maximum 3 photos per entry.',
  'validation/invalid-file-type': 'Only JPEG, PNG, and WebP images are allowed.',
  'validation/file-too-large': 'File is too large. Maximum 10MB per photo.',

  // Storage errors
  'storage/unauthorized': 'You do not have permission to access this photo.',
  'storage/quota-exceeded': 'Storage quota exceeded. Please delete old photos.',
  'storage/object-not-found': 'Photo not found.',
  'storage/upload-failed': 'Failed to upload photo. Please try again.',

  // Firestore errors
  'firestore/permission-denied': 'You do not have permission to access this data.',
  'firestore/not-found': 'Entry not found.',
  'firestore/unavailable': 'Service temporarily unavailable. Please try again.',
  'firestore/resource-exhausted': 'Too many requests. Please wait a moment and try again.',

  // Rate limiting
  'quota-exceeded': 'Service limit reached. Please try again in a few minutes.',
  'resource-exhausted': 'Too many requests. Please slow down and try again.',

  // Optimization errors
  'optimization/failed': 'Failed to process image. Please try a different file.',

  // Default
  default: 'An unexpected error occurred. Please try again.',
};

/**
 * Creates an AppError with user-friendly message
 * @param code Error code (e.g., 'auth/invalid-email')
 * @param originalError Original error object for logging
 * @returns AppError with mapped message
 */
export function createAppError(code: string, originalError?: Error): AppError {
  const message = ERROR_MESSAGES[code] || ERROR_MESSAGES['default'];
  const error = new Error(message) as AppError;
  error.code = code;
  error.original = originalError;
  return error;
}

/**
 * Handles Firebase errors and returns user-friendly AppError
 * Also detects rate limiting and quota exceeded errors
 * @param error Firebase error object
 * @returns AppError with mapped message
 */
export function handleFirebaseError(error: unknown): AppError {
  if (error instanceof Error) {
    const firebaseError = error as unknown as Record<string, unknown>;
    let code = (firebaseError.code as string) || 'unknown';

    // Check for rate limiting / quota exceeded
    if (
      code === 'resource-exhausted' ||
      code === 'quota-exceeded' ||
      code.includes('quota') ||
      code.includes('rate-limit')
    ) {
      code = 'quota-exceeded';
    }

    return createAppError(code, error);
  }

  return createAppError('default', new Error(String(error)));
}

/**
 * Alias for handleFirebaseError for clearer intent in service methods
 */
export const mapFirebaseError = handleFirebaseError;

/**
 * Logs error for debugging (console in dev, external service in prod)
 * @param error Error to log
 * @param context Additional context for debugging
 */
export function logError(error: Error, context?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'development') {
    console.error('[Error]', error.message, context);
  } else {
    // In production, send to Firebase Crashlytics or similar service
    // TODO: Implement production error tracking
  }
}

/**
 * Type guard for AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof Error && 'code' in error;
}
